
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { UserMenu } from "@/components/ui/user-menu";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalTranslation } from "@/components/GlobalTranslationProvider";
import { useSettings } from "@/contexts/SettingsContext";
import { Badge } from "@/components/ui/badge";
import { Zap } from "lucide-react";
import { useCentralizedPersona } from "@/hooks/useCentralizedPersona";
import { NotificationBell } from "@/components/quotes/NotificationBell";
import { ViewSwitcher } from "@/components/ViewSwitcher";

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useGlobalTranslation();
  const { isDemoMode } = useSettings();
  const { persona, displayName, isViewAppropriate } = useCentralizedPersona();
  
  // Enhanced navigation items based on current route and persona
  const getNavigationItems = () => {
    const path = location.pathname;
    
    // Persona-specific navigation items
    const personaNavItems = {
      controller: [
        { label: t('nav.receivables', 'Receivables'), route: '/receivables' },
        { label: t('nav.payables', 'Payables'), route: '/payables' },
        { label: t('nav.approvals', 'Approvals'), route: '/approvals' }
      ],
      analyst: [
        { label: t('nav.analytics', 'Analytics'), route: '/analytics' },
        { label: t('nav.forex', 'Forex'), route: '/forex' },
        { label: t('nav.usmcaCompliance', 'USMCA Compliance'), route: '/usmca-compliance' },
        { label: t('nav.documents', 'Documents'), route: '/documents' }
      ],
      ceo: [
        { label: t('nav.dashboard', 'Dashboard'), route: '/dashboard' },
        { label: t('nav.analytics', 'Analytics'), route: '/analytics' },
        { label: t('nav.suppliers', 'Suppliers'), route: '/suppliers' },
        { label: t('nav.buyers', 'Buyers'), route: '/buyers' },
        { label: t('nav.settings', 'Settings'), route: '/settings' }
      ],
      cfo: [
        { label: t('nav.dashboard', 'Dashboard'), route: '/dashboard' },
        { label: t('nav.analytics', 'Analytics'), route: '/analytics' },
        { label: t('nav.forex', 'Forex'), route: '/forex' },
        { label: t('nav.settings', 'Settings'), route: '/settings' }
      ]
    };
    
    if (path === "/" || path === "/dashboard") {
      return [
        { label: t('nav.keyChanges', 'Key Changes'), active: true, onClick: () => navigate("/") },
        { label: t('nav.whatsDrivingIt', "What's Driving It"), active: false, onClick: () => navigate("/analytics") },
        { label: t('nav.takeAction', 'Take Action'), active: false, onClick: () => navigate("/messages") }
      ];
    }
    
    if (path.startsWith("/messages")) {
      return [
        { label: t('nav.keyChanges', 'Key Changes'), active: false, onClick: () => navigate("/") },
        { label: t('nav.whatsDrivingIt', "What's Driving It"), active: false, onClick: () => navigate("/analytics") },
        { label: t('nav.takeAction', 'Take Action'), active: true, onClick: () => navigate("/messages") }
      ];
    }
    
    if (path.startsWith("/contracts")) {
      return [
        { label: t('nav.allContracts', 'All Contracts'), active: path === "/contracts", onClick: () => navigate("/contracts") },
        { label: t('nav.upload', 'Upload'), active: false, onClick: () => navigate("/contracts") },
        { label: t('nav.analysis', 'Analysis'), active: false, onClick: () => navigate("/contracts") }
      ];
    }
    
    if (path.startsWith("/receivables")) {
      return [
        { label: t('nav.receivables', 'Receivables'), active: true, onClick: () => navigate("/receivables") },
        { label: t('nav.invoices', 'Invoices'), active: false, onClick: () => navigate("/receivables") },
        { label: t('nav.credits', 'Credits'), active: false, onClick: () => navigate("/receivables/credits") }
      ];
    }
    
    // Payments In page removed — Receivables covers incoming payment tracking
    
    if (path.startsWith("/analytics")) {
      return [
        { label: t('nav.keyChanges', 'Key Changes'), active: false, onClick: () => navigate("/") },
        { label: t('nav.whatsDrivingIt', "What's Driving It"), active: true, onClick: () => navigate("/analytics") },
        { label: t('nav.takeAction', 'Take Action'), active: false, onClick: () => navigate("/messages") }
      ];
    }
    
    if (path.startsWith("/forex")) {
      return [
        { label: t('nav.forex', 'Forex Dashboard'), active: path === "/forex", onClick: () => navigate("/forex") },
        { label: t('nav.hedging', 'Hedging'), active: false, onClick: () => navigate("/forex") },
        { label: t('nav.rates', 'Exchange Rates'), active: false, onClick: () => navigate("/forex") }
      ];
    }
    
    if (path.startsWith("/suppliers")) {
      return [
        { label: t('nav.allSuppliers', 'All Suppliers'), active: path === "/suppliers", onClick: () => navigate("/suppliers") },
        { label: t('nav.supplierOnboarding', 'Onboarding'), active: path === "/suppliers/onboarding", onClick: () => navigate("/suppliers/onboarding") },
        { label: t('nav.supplierAnalytics', 'Analytics'), active: path === "/suppliers/analytics", onClick: () => navigate("/suppliers/analytics") },
        { label: t('nav.supplierContracts', 'Contracts'), active: path === "/suppliers/contracts", onClick: () => navigate("/suppliers/contracts") }
      ];
    }
    
    if (path.startsWith("/buyers")) {
      return [
        { label: t('nav.allBuyers', 'All Buyers'), active: path === "/buyers", onClick: () => navigate("/buyers") },
        { label: t('nav.buyerAnalytics', 'Analytics'), active: false, onClick: () => navigate("/buyers") },
        { label: t('nav.buyerContracts', 'Contracts'), active: false, onClick: () => navigate("/buyers") }
      ];
    }
    
    if (path.startsWith("/payables")) {
      return [
        { label: t('nav.payables', 'Payables'), active: true, onClick: () => navigate("/payables") },
        { label: t('nav.suppliers', 'Suppliers'), active: false, onClick: () => navigate("/suppliers") },
        { label: t('nav.approvals', 'Approvals'), active: false, onClick: () => navigate("/approvals") },
        // Payments Out removed — Payables covers outgoing payment tracking
      ];
    }
    
    // Payments Out page removed — Payables covers outgoing payment tracking
    
    if (path.startsWith("/approvals")) {
      return [
        { label: t('nav.allApprovals', 'All Approvals'), active: path === "/approvals", onClick: () => navigate("/approvals") },
        { label: t('nav.pendingApprovals', 'Pending'), active: false, onClick: () => navigate("/approvals") },
        { label: t('nav.approvalHistory', 'History'), active: false, onClick: () => navigate("/approvals") }
      ];
    }
    
    if (path.startsWith("/documents")) {
      return [
        { label: t('nav.allDocuments', 'All Documents'), active: path === "/documents", onClick: () => navigate("/documents") },
        { label: t('nav.upload', 'Upload'), active: false, onClick: () => navigate("/documents") },
        { label: t('nav.documentAnalytics', 'Analytics'), active: false, onClick: () => navigate("/documents") }
      ];
    }
    
    if (path.startsWith("/usmca-compliance")) {
      return [
        { label: t('nav.usmcaDashboard', 'USMCA Dashboard'), active: path === "/usmca-compliance", onClick: () => navigate("/usmca-compliance") },
        { label: t('nav.complianceStatus', 'Compliance Status'), active: false, onClick: () => navigate("/usmca-compliance") },
        { label: t('nav.supplierMapping', 'Supplier Mapping'), active: false, onClick: () => navigate("/usmca-compliance") }
      ];
    }
    
    if (path.startsWith("/priorities")) {
      return [
        { label: t('nav.priorities', 'Priorities'), active: path === "/priorities", onClick: () => navigate("/priorities") },
        { label: t('nav.prioritySettings', 'Settings'), active: false, onClick: () => navigate("/priorities") },
        { label: t('nav.priorityAnalysis', 'Analysis'), active: false, onClick: () => navigate("/priorities") }
      ];
    }
    
    if (path.startsWith("/settings")) {
      return [
        { label: t('nav.general', 'General'), active: path === "/settings", onClick: () => navigate("/settings") },
        { label: t('nav.users', 'Users'), active: path === "/settings/users", onClick: () => navigate("/settings/users") },
        { label: t('nav.integrations', 'Integrations'), active: path === "/settings/integrations", onClick: () => navigate("/settings/integrations") }
      ];
    }
    
    // Default navigation
    return [
      { label: t('nav.overview', 'Overview'), active: true, onClick: () => navigate(path) },
      { label: t('nav.details', 'Details'), active: false, onClick: () => navigate(`${path}/details`) },
      { label: t('nav.actions', 'Actions'), active: false, onClick: () => navigate(`${path}/actions`) }
    ];
  };

  const navigationItems = getNavigationItems();

  return (
    <header className="border-b bg-background px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <SidebarTrigger className="hover:text-brand-pink hover:bg-transparent flex-shrink-0" />
          <nav className="flex items-center gap-6 overflow-x-auto">
            {navigationItems.map((item, index) => (
              <span 
                key={item.label}
                onClick={item.onClick}
                className={`text-sm font-medium cursor-pointer transition-colors whitespace-nowrap hover:text-brand-pink ${
                  item.active 
                    ? "text-brand-pink" 
                    : "text-muted-foreground"
                }`}
              >
                {item.label}
              </span>
            ))}
          </nav>
        </div>
        
        <div className="flex items-center gap-4 flex-shrink-0">
          {/* View Switcher - Internal Personas + Partner Portals */}
          <ViewSwitcher />

          {isDemoMode && (
            <Badge variant="secondary" className="bg-warning-muted text-warning border-warning/30">
              <Zap className="h-3 w-3 mr-1" />
              {t('settings.simulation', 'Demo Mode')}
            </Badge>
          )}

          <NotificationBell />

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search', 'Search...')}
              className="pl-10 w-64"
            />
          </div>

          <UserMenu />
        </div>
      </div>
    </header>
  );
}
